import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import { formatCount, formatRelative } from "@/utils/format";
import { useFavorites } from "@/hooks/useFavorites";

interface VideoCardProps {
  video: Video;
  className?: string;
}

export default function VideoCard({ video, className = "" }: VideoCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(video.id);

  const handleFavorite = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(video.id);
  };

  return (
    <Link
      to={`/detail/${video.id}`}
      className={`group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition hover:border-primary-200 ${className}`}
    >
      <div className="relative aspect-video w-full overflow-hidden bg-background-200">
        <img
          src={video.cover}
          alt={`${video.title} 视频封面`}
          title={`${video.title} - 91短视频`}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-foreground-950/0 transition group-hover:bg-foreground-950/25">
          <span className="flex h-12 w-12 scale-75 items-center justify-center rounded-full bg-background-50/90 text-primary-600 opacity-0 transition duration-300 group-hover:scale-100 group-hover:opacity-100">
            <i className="ri-play-fill text-2xl"></i>
          </span>
        </div>
        <span className="absolute bottom-2 right-2 rounded bg-foreground-950/75 px-1.5 py-0.5 text-xs font-medium text-background-50">
          {video.duration}
        </span>
        <button
          type="button"
          onClick={handleFavorite}
          aria-label={favorited ? "取消收藏" : "收藏视频"}
          className={`absolute right-2 top-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full backdrop-blur transition ${
            favorited
              ? "bg-accent-500 text-background-50"
              : "bg-foreground-950/45 text-background-50 hover:bg-foreground-950/70"
          }`}
        >
          <i className={`${favorited ? "ri-heart-fill" : "ri-heart-line"} text-base`}></i>
        </button>
      </div>

      <div className="flex flex-1 flex-col p-3">
        <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition group-hover:text-primary-600">
          {video.title}
        </h3>
        <div className="mt-2 flex items-center gap-2 text-xs text-foreground-500">
          <img
            src={video.authorAvatar}
            alt={`${video.author} 头像`}
            title={`${video.author} - 91短视频`}
            className="h-5 w-5 rounded-full object-cover"
          />
          <span className="truncate">{video.author}</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-play-circle-line text-sm"></i>
            {formatCount(video.plays)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line text-sm"></i>
            {formatCount(video.comments)}
          </span>
          <span className="whitespace-nowrap">{formatRelative(video.uploadDate)}</span>
        </div>
      </div>
    </Link>
  );
}